/**
 * Utility functions
 * Shared helper functions for LostMind AI Video Lab
 */

import { clsx, type ClassValue } from 'clsx';

/** Combine CSS classes with conditional logic */
export function cn(...inputs: ClassValue[]) {
  return clsx(inputs);
}

/** Format duration in seconds to MM:SS or HH:MM:SS */
export function formatDuration(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
}

/** Extract YouTube video ID from various URL formats */
export function extractYouTubeVideoId(url: string): string | null {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/,
    /youtube\.com\/embed\/([^&\n?#]+)/,
    /youtube\.com\/v\/([^&\n?#]+)/,
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) {
      return match[1];
    }
  }

  return null;
}

/** Validate YouTube URL */
export function isValidYouTubeUrl(url: string): boolean {
  return extractYouTubeVideoId(url) !== null;
}

/** Generate a random ID */
export function generateId(): string {
  return Math.random().toString(36).substring(2) + Date.now().toString(36);
}

/** Debounce function calls */
export function debounce<T extends (...args: unknown[]) => unknown>(
  func: T,
  wait: number
): T {
  let timeout: NodeJS.Timeout;
  
  return (function(this: ThisParameterType<T>, ...args: Parameters<T>) {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), wait);
  }) as T;
}

/** Throttle function calls */
export function throttle<T extends (...args: unknown[]) => unknown>(
  func: T,
  limit: number
): T {
  let inThrottle: boolean;
  
  return (function(this: ThisParameterType<T>, ...args: Parameters<T>) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  }) as T;
}

/** Format file size in bytes to human readable */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

/** Format date for display */
export function formatDate(date: string | Date): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('en-AU', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Calculate credits needed for operation */
export function calculateCredits(operation: string, extras: Record<string, unknown> = {}): number {
  const baseCosts = {
    'key-moments': 1,
    'excitement-chart': 1,
    'summary': 1,
    'quiz': 2,
    'learning-app': 5,
    'notes': 1,
  };

  const baseCost = baseCosts[operation as keyof typeof baseCosts] || 1;
  
  // Add complexity multipliers if needed
  if (extras.longVideo && (extras.duration as number) > 3600) {
    return Math.ceil(baseCost * 1.5);
  }
  
  return baseCost;
}

/** Truncate text with ellipsis */
export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - 3) + '...';
}

/** Check if user has sufficient credits */
export function hasCredits(userCredits: number, requiredCredits: number): boolean {
  return userCredits >= requiredCredits;
}

/** Convert timecode string (MM:SS or HH:MM:SS) to seconds */
export function timeToSecs(timecode: string): number {
  const split = timecode.split(':').map(parseFloat);

  return split.length === 2
    ? split[0] * 60 + split[1]
    : split[0] * 3600 + split[1] * 60 + split[2];
}

/** Color palette for charts and visualizations */
export const CHART_COLORS = {
  primary: '#1a365d',
  secondary: '#00d9ff', 
  accent: '#ffd700',
  success: '#38a169',
  warning: '#d69e2e',
  error: '#e53e3e',
  neutral: '#718096',
};